/**
 * 帮助中心静态数据管理
 * 所有FAQ内容集中在此文件维护
 * 数据来源：Notion 帮助中心文档
 *
 * 数据结构说明：
 * - 每个文章由 questionKey（标题）和 blocks（内容块数组）组成
 * - blocks 中 type='subtitle' 表示副标题样式，type='text' 表示正文样式
 * - 所有文本内容通过 i18n 键引用，翻译文件位于 i18n/locales/help.*.json
 */

export type ContentBlockType = 'subtitle' | 'text'

export interface ContentBlock {
  /** 内容块类型：subtitle=副标题样式，text=正文样式 */
  type: ContentBlockType
  /** i18n 翻译键 */
  key: string
}

export interface HelpArticle {
  id: string
  /** 问题标题的 i18n 键 */
  questionKey: string
  /** 内容块列表，按顺序渲染 */
  blocks: ContentBlock[]
}

export interface HelpCategory {
  key: string
  /** 分类标签的 i18n 键 */
  labelKey: string
  articles: HelpArticle[]
}

/**
 * 帮助中心分类及文章数据
 */
export const helpCategories: HelpCategory[] = [
  // ======================== 一、账户 ========================
  {
    key: 'account',
    labelKey: 'help_cat_account',
    articles: [
      {
        id: 'account-1',
        questionKey: 'help_account_register_q',
        blocks: [
          { type: 'subtitle', key: 'help_account_register_s1' },
          { type: 'text', key: 'help_account_register_t1' },
          { type: 'text', key: 'help_account_register_t2' }
        ]
      },
      {
        id: 'account-2',
        questionKey: 'help_account_kyc_reason_q',
        blocks: [
          { type: 'subtitle', key: 'help_account_kyc_reason_s1' },
          { type: 'text', key: 'help_account_kyc_reason_t1' },
          { type: 'subtitle', key: 'help_account_kyc_reason_s2' },
          { type: 'text', key: 'help_account_kyc_reason_t2' }
        ]
      },
      {
        id: 'account-3',
        questionKey: 'help_account_kyc_fail_q',
        blocks: [
          { type: 'subtitle', key: 'help_account_kyc_fail_s1' },
          { type: 'text', key: 'help_account_kyc_fail_t1' },
          { type: 'text', key: 'help_account_kyc_fail_t2' }
        ]
      },
      {
        id: 'account-4',
        questionKey: 'help_account_edit_profile_q',
        blocks: [
          { type: 'text', key: 'help_account_edit_profile_t1' },
          { type: 'text', key: 'help_account_edit_profile_t2' },
          { type: 'text', key: 'help_account_edit_profile_t3' }
        ]
      }
    ]
  },

  // ======================== 二、账户安全 ========================
  {
    key: 'security',
    labelKey: 'help_cat_security',
    articles: [
      {
        id: 'security-1',
        questionKey: 'help_security_protect_q',
        blocks: [
          { type: 'subtitle', key: 'help_security_protect_s1' },
          { type: 'text', key: 'help_security_protect_t1' }
        ]
      },
      {
        id: 'security-2',
        questionKey: 'help_security_forgot_pwd_q',
        blocks: [
          { type: 'subtitle', key: 'help_security_forgot_pwd_s1' },
          { type: 'text', key: 'help_security_forgot_pwd_t1' }
        ]
      },
      {
        id: 'security-3',
        questionKey: 'help_security_restricted_q',
        blocks: [
          { type: 'subtitle', key: 'help_security_restricted_s1' },
          { type: 'text', key: 'help_security_restricted_t1' },
          { type: 'text', key: 'help_security_restricted_t2' }
        ]
      }
    ]
  },

  // ======================== 三、卡片服务 ========================
  {
    key: 'card',
    labelKey: 'help_cat_card',
    articles: [
      {
        id: 'card-1',
        questionKey: 'help_card_activate_q',
        blocks: [
          { type: 'subtitle', key: 'help_card_activate_s1' },
          { type: 'text', key: 'help_card_activate_t1' },
          { type: 'text', key: 'help_card_activate_t2' }
        ]
      },
      {
        id: 'card-2',
        questionKey: 'help_card_txn_fail_q',
        blocks: [
          { type: 'subtitle', key: 'help_card_txn_fail_s1' },
          { type: 'text', key: 'help_card_txn_fail_t1' },
          { type: 'text', key: 'help_card_txn_fail_t2' }
        ]
      },
      {
        id: 'card-3',
        questionKey: 'help_card_digital_wallet_q',
        blocks: [
          { type: 'subtitle', key: 'help_card_digital_wallet_s1' },
          { type: 'text', key: 'help_card_digital_wallet_t1' },
          { type: 'text', key: 'help_card_digital_wallet_t2' },
          { type: 'text', key: 'help_card_digital_wallet_t3' }
        ]
      },
      {
        id: 'card-4',
        questionKey: 'help_card_lost_q',
        blocks: [
          { type: 'subtitle', key: 'help_card_lost_s1' },
          { type: 'text', key: 'help_card_lost_t1' }
        ]
      }
    ]
  },

  // ======================== 四、充值与提现 ========================
  {
    key: 'deposit',
    labelKey: 'help_cat_deposit',
    articles: [
      {
        id: 'deposit-1',
        questionKey: 'help_deposit_how_to_q',
        blocks: [
          { type: 'subtitle', key: 'help_deposit_how_to_s1' },
          { type: 'text', key: 'help_deposit_how_to_t1' },
          { type: 'text', key: 'help_deposit_how_to_t2' }
        ]
      },
      {
        id: 'deposit-2',
        questionKey: 'help_deposit_arrival_time_q',
        blocks: [
          { type: 'subtitle', key: 'help_deposit_arrival_time_s1' },
          { type: 'text', key: 'help_deposit_arrival_time_t1' },
          { type: 'text', key: 'help_deposit_arrival_time_t2' }
        ]
      },
      {
        id: 'deposit-3',
        questionKey: 'help_deposit_pending_q',
        blocks: [
          { type: 'subtitle', key: 'help_deposit_pending_s1' },
          { type: 'text', key: 'help_deposit_pending_t1' }
        ]
      }
    ]
  },

  // ======================== 五、数字资产服务 ========================
  {
    key: 'digital-assets',
    labelKey: 'help_cat_digital_assets',
    articles: [
      {
        id: 'asset-1',
        questionKey: 'help_asset_deposit_q',
        blocks: [
          { type: 'subtitle', key: 'help_asset_deposit_s1' },
          { type: 'text', key: 'help_asset_deposit_t1' },
          { type: 'text', key: 'help_asset_deposit_t2' }
        ]
      },
      {
        id: 'asset-2',
        questionKey: 'help_asset_slow_txn_q',
        blocks: [
          { type: 'subtitle', key: 'help_asset_slow_txn_s1' },
          { type: 'text', key: 'help_asset_slow_txn_t1' },
          { type: 'text', key: 'help_asset_slow_txn_t2' }
        ]
      },
      {
        id: 'asset-3',
        questionKey: 'help_asset_reverse_q',
        blocks: [
          { type: 'subtitle', key: 'help_asset_reverse_s1' },
          { type: 'text', key: 'help_asset_reverse_t1' },
          { type: 'subtitle', key: 'help_asset_reverse_s2' },
          { type: 'text', key: 'help_asset_reverse_t2' }
        ]
      }
    ]
  },

  // ======================== 六、风控与合规 ========================
  {
    key: 'risk',
    labelKey: 'help_cat_risk',
    articles: [
      {
        id: 'risk-1',
        questionKey: 'help_risk_review_q',
        blocks: [
          { type: 'subtitle', key: 'help_risk_review_s1' },
          { type: 'text', key: 'help_risk_review_t1' },
          { type: 'text', key: 'help_risk_review_t2' }
        ]
      },
      {
        id: 'risk-2',
        questionKey: 'help_risk_frozen_q',
        blocks: [
          { type: 'subtitle', key: 'help_risk_frozen_s1' },
          { type: 'text', key: 'help_risk_frozen_t1' },
          { type: 'text', key: 'help_risk_frozen_t2' }
        ]
      }
    ]
  },

  // ======================== 七、费用 ========================
  {
    key: 'fees',
    labelKey: 'help_cat_fees',
    articles: [
      {
        id: 'fees-1',
        questionKey: 'help_fees_service_q',
        blocks: [
          { type: 'subtitle', key: 'help_fees_service_s1' },
          { type: 'text', key: 'help_fees_service_t1' },
          { type: 'text', key: 'help_fees_service_t2' }
        ]
      }
    ]
  }
]

/**
 * 获取所有文章的扁平列表，用于搜索功能
 */
export function getAllArticles(): (HelpArticle & { categoryLabelKey: string })[] {
  const articles: (HelpArticle & { categoryLabelKey: string })[] = []
  for (const category of helpCategories) {
    for (const article of category.articles) {
      articles.push({
        ...article,
        categoryLabelKey: category.labelKey
      })
    }
  }
  return articles
}
